import { createFileRoute, Link } from '@tanstack/react-router';
import { useState, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { conversationApi, type ConversationStatus } from '@/lib/api';
import { MessageSquare, Search, ExternalLink, Circle, Zap } from 'lucide-react';
import { Spinner } from '@/components/ui/Spinner';
import { PageGuide } from '@/components/PageGuide';
import { usePortalSocketEvent } from '@/contexts/PortalSocketContext';

export const Route = createFileRoute('/_auth/conversations/')({ component: ConversationsPage });

const STATUS_OPTIONS: Array<{ value: ConversationStatus | ''; label: string }> = [
  { value: '', label: 'All' },
  { value: 'OPEN', label: 'Open' },
  { value: 'WAITING_FOR_AGENT', label: 'Waiting' },
  { value: 'RESOLVED', label: 'Resolved' },
  { value: 'ABANDONED', label: 'Abandoned' },
];

const STATUS_STYLE: Record<string, { dot: string; label: string }> = {
  OPEN:               { dot: '#34d399', label: 'Open' },
  WAITING_FOR_AGENT:  { dot: '#f87171', label: 'Waiting for agent' },
  WAITING_FOR_USER:   { dot: '#fbbf24', label: 'Waiting for user' },
  RESOLVED:           { dot: '#475569', label: 'Resolved' },
  ABANDONED:          { dot: '#475569', label: 'Abandoned' },
};

const CHANNEL_STYLE: Record<string, { bg: string; text: string }> = {
  CHAT:   { bg: '#6366f120', text: '#818cf8' },
  EMAIL:  { bg: '#0ea5e920', text: '#38bdf8' },
  VOICE:  { bg: '#10b98120', text: '#34d399' },
  API:    { bg: '#64748b20', text: '#94a3b8' },
};

export default function ConversationsPage() {
  const [statusFilter, setStatusFilter] = useState<ConversationStatus | ''>('');
  const [search, setSearch] = useState('');
  const [newCount, setNewCount] = useState(0);
  const queryClient = useQueryClient();

  const { data: conversations = [], isLoading } = useQuery({
    queryKey: ['conversations', statusFilter],
    queryFn: () => conversationApi.list({ status: statusFilter || undefined, limit: 100 }),
  });

  // ── Realtime: auto-refresh when new conversations arrive ────────────────
  const handleConversationNew = useCallback(() => {
    setNewCount(n => n + 1);
    void queryClient.invalidateQueries({ queryKey: ['conversations'] });
  }, [queryClient]);

  const handleConversationUpdated = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['conversations'] });
  }, [queryClient]);

  usePortalSocketEvent('conversation:new', handleConversationNew);
  usePortalSocketEvent('conversation:updated', handleConversationUpdated);

  const filtered = conversations.filter((c) =>
    !search || c.agent?.name?.toLowerCase().includes(search.toLowerCase()) || c.channel.toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <div className="page">
      <PageGuide pageKey="conversations" />

      {/* ── Header ── */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', marginBottom: 24 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#e2e8f0' }}>Conversations</h1>
            {newCount > 0 && (
              <button
                onClick={() => setNewCount(0)}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 5,
                  padding: '2px 9px', borderRadius: 20, fontSize: 11, fontWeight: 700,
                  background: '#6366f120', color: '#818cf8', border: '1px solid #6366f140',
                  cursor: 'pointer', animation: 'pulse 2s infinite',
                }}
                title="New conversations arrived — click to dismiss"
              >
                <Zap size={10} /> {newCount} new
              </button>
            )}
          </div>
          <p style={{ color: '#64748b', fontSize: 13, marginTop: 4 }}>
            {isLoading ? '—' : `${filtered.length} conversation${filtered.length !== 1 ? 's' : ''}`} · Monitor all AI conversations across your tenant
          </p>
        </div>
      </div>

      {/* ── Filters ── */}
      <div style={{ display: 'flex', gap: 10, marginTop: 20, alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 200, maxWidth: 300 }}>
          <Search size={14} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#475569', pointerEvents: 'none' }} />
          <input
            id="conversations-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by agent or channel…"
            style={{ width: '100%', padding: '8px 12px 8px 34px', background: '#0f1221', border: '1px solid #1e2130', borderRadius: 8, color: '#e2e8f0', fontSize: 13, outline: 'none', boxSizing: 'border-box' }}
          />
        </div>
        {STATUS_OPTIONS.map((o) => (
          <button key={o.value || 'ALL'} onClick={() => setStatusFilter(o.value)} style={{
            padding: '6px 13px', borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: 'pointer',
            border: '1px solid',
            borderColor: statusFilter === o.value ? 'var(--color-primary)' : '#1e2130',
            background: statusFilter === o.value ? 'var(--color-primary-dim)' : 'transparent',
            color: statusFilter === o.value ? 'var(--color-primary)' : '#64748b',
          }}>
            {o.label}
          </button>
        ))}
      </div>

      {isLoading && <div style={{ padding: '40px 0', textAlign: 'center' }}><Spinner size="md" /></div>}

      {!isLoading && filtered.length === 0 && (
        <div style={{ padding: '56px 0', textAlign: 'center', border: '1px dashed #1e2130', borderRadius: 12, color: '#475569' }}>
          <MessageSquare size={32} style={{ opacity: 0.3, marginBottom: 12 }} />
          <p style={{ margin: 0, fontSize: 14 }}>No conversations found</p>
        </div>
      )}

      {!isLoading && filtered.length > 0 && (
        <div style={{ overflowX: 'auto', border: '1px solid #1a1f35', borderRadius: 12 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #1a1f35' }}>
                {['Status', 'Agent', 'Channel', 'Messages', 'Turns', 'Started', ''].map((h) => (
                  <th key={h} style={{ padding: '11px 16px', textAlign: 'left', color: '#475569', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((conv) => {
                const s = STATUS_STYLE[conv.status] ?? { dot: '#475569', label: conv.status };
                const ch = CHANNEL_STYLE[conv.channel] ?? CHANNEL_STYLE.API;
                return (
                  <tr key={conv.id} className="tableRow" style={{ borderBottom: '1px solid #12172a' }}>
                    <td style={{ padding: '12px 16px' }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 6, color: s.dot, fontSize: 12, fontWeight: 600 }}>
                        <Circle size={7} fill="currentColor" /> {s.label}
                      </span>
                    </td>
                    <td style={{ padding: '12px 16px', color: '#e2e8f0', fontWeight: 500 }}>
                      {conv.agent?.name ?? <span style={{ color: '#475569' }}>No agent</span>}
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <span style={{ padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: ch.bg, color: ch.text }}>{conv.channel}</span>
                    </td>
                    <td style={{ padding: '12px 16px', color: '#94a3b8', fontVariantNumeric: 'tabular-nums' }}>{conv._count?.messages ?? '—'}</td>
                    <td style={{ padding: '12px 16px', color: '#94a3b8', fontVariantNumeric: 'tabular-nums' }}>{conv.turnCount}</td>
                    <td style={{ padding: '12px 16px', color: '#64748b', fontSize: 12, whiteSpace: 'nowrap' }}>
                      {new Date(conv.createdAt).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      {/* Open in new tab ↗ */}
                      <Link
                        to="/conversations/$conversationId"
                        params={{ conversationId: conv.id }}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '5px 10px', border: '1px solid #2d3148', borderRadius: 7, color: '#94a3b8', fontSize: 12, textDecoration: 'none' }}
                      >
                        Open <ExternalLink size={11} />
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <style>{`.tableRow:hover { background: #0f1221 !important; } @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:.7} }`}</style>
    </div>
  );
}
